import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import AutoScroll from 'embla-carousel-auto-scroll';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { mediaUrl } from '@/lib/api';
import { imgProps } from '@/lib/img';
import { Reveal } from './Motion';

export const GalleryCarousel = ({ items = [], showCaptions = true, autoScroll = true, grayscale = true, size = 'default', speed = 0.9, controls = true, className = '', captionStyle = 'default', lightboxCaptionStyle = 'default' }) => {
  const reduce = useReducedMotion();
  // Drop images that fail to load so the strip never shows blank slots.
  const [broken, setBroken] = useState({});
  const visible = items.filter((g) => g?.url && !broken[g.url]);
  // Always loop back to the first photo. Embla can only loop when the slides are wider than the viewport,
  // so with few photos we repeat them (clones are aria-hidden) until the track is long enough.
  const minSlides = size === 'small' ? 10 : size === 'wide' ? 6 : 8;
  const canLoop = visible.length > 1;
  const reps = canLoop ? Math.max(1, Math.ceil(minSlides / visible.length)) : 1;
  const slides = [];
  for (let r = 0; r < reps; r += 1) visible.forEach((g, i) => slides.push({ ...g, _i: i, _clone: r > 0 }));
  const [emblaRef, embla] = useEmblaCarousel(
    { align: 'start', dragFree: true, containScroll: canLoop ? false : 'trimSnaps', loop: canLoop, skipSnaps: true },
    autoScroll && !reduce && canLoop
      ? [AutoScroll({ speed, startDelay: 1200, stopOnInteraction: false, stopOnMouseEnter: true, stopOnFocusIn: true })]
      : []
  );
  const sizeCls = size === 'small'
    ? 'w-[58vw] sm:w-[260px] lg:w-[300px]'
    : size === 'medium'
      ? 'w-[70vw] sm:w-[340px] lg:w-[380px]'
      : size === 'wide'
        ? 'w-[82vw] sm:w-[520px] lg:w-[640px]'
        : 'w-[72vw] sm:w-[380px] lg:w-[420px]';
  const ratioCls = size === 'small' ? 'aspect-square' : size === 'medium' ? 'aspect-[4/3]' : size === 'wide' ? 'aspect-[4/3]' : 'aspect-[4/5] sm:aspect-[3/4]';
  const [lightbox, setLightbox] = useState(null); // index
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setCanPrev(canLoop || embla.canScrollPrev());
    setCanNext(canLoop || embla.canScrollNext());
  }, [embla, canLoop]);

  // Arrow click: step one slide; in loop mode Embla wraps back to the first photo, never into empty space.
  const go = (dir) => {
    if (!embla) return;
    if (dir > 0) {
      if (embla.canScrollNext()) embla.scrollNext();
      else embla.scrollTo(0);
    } else if (embla.canScrollPrev()) embla.scrollPrev();
    else embla.scrollTo(embla.scrollSnapList().length - 1);
  };

  useEffect(() => {
    if (!embla) return undefined;
    onSelect();
    embla.on('select', onSelect);
    embla.on('reInit', onSelect);
    return () => {
      embla.off('select', onSelect);
    };
  }, [embla, onSelect]);

  // Pause the auto-scroll while the lightbox is open, resume after.
  useEffect(() => {
    const plugin = embla?.plugins?.()?.autoScroll;
    if (!plugin) return;
    if (lightbox !== null) plugin.stop();
    else plugin.play();
  }, [embla, lightbox]);

  const close = () => setLightbox(null);
  const step = useCallback((d) => setLightbox((i) => (i === null ? null : (i + d + visible.length) % visible.length)), [visible.length]);

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, step]);

  // touch swipe in lightbox
  const [touchX, setTouchX] = useState(null);

  if (!visible.length) {
    return <p className="text-sm text-muted-foreground">Gallery images will appear here soon.</p>;
  }
  const captionCls = captionStyle === 'small'
    ? 'absolute inset-x-0 bottom-0 px-3 py-2.5 text-left text-white/90 text-[10px] tracking-[0.16em] uppercase bg-gradient-to-t from-black/55 to-transparent'
    : 'absolute inset-x-0 bottom-0 p-4 text-left text-white text-xs tracking-[0.14em] uppercase bg-gradient-to-t from-black/50 to-transparent';

  return (
    <div data-testid="gallery-carousel" className={`relative group/carousel ${className}`}>
      <div className="overflow-hidden -mx-5 sm:mx-0" ref={emblaRef}>
        <div className="flex gap-3 sm:gap-5 pl-5 sm:pl-0 pr-5 sm:pr-0">
          {slides.map((g, k) => {
            const i = g._i;
            return (
            <Reveal key={`${g.id || i}-${k}`} delay={g._clone ? 0 : Math.min(i * 0.05, 0.3)} className={`shrink-0 ${sizeCls}`} aria-hidden={g._clone || undefined}>
              <button
                type="button"
                onClick={() => setLightbox(i)}
                tabIndex={g._clone ? -1 : 0}
                className={`${grayscale ? 'gallery-thumb' : 'color-thumb'} group relative block w-full ${ratioCls} overflow-hidden rounded-xl bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]`}
                data-testid={g._clone ? 'gallery-thumbnail-clone' : 'gallery-thumbnail'}
                aria-label={`Open image ${i + 1}${g.caption ? `: ${g.caption}` : ''}`}
              >
                <img {...imgProps(g.url, { widths: [320, 480, 800], sizes: size === 'small' ? '(min-width: 1024px) 300px, (min-width: 640px) 260px, 58vw' : size === 'wide' ? '(min-width: 1024px) 640px, (min-width: 640px) 520px, 82vw' : '(min-width: 1024px) 420px, (min-width: 640px) 380px, 72vw' })} alt={g.alt || g.caption || `${g.category || 'Resort'} — Bukit Ancak Lembongan, Nusa Lembongan`} loading="lazy" decoding="async" onError={() => setBroken((b) => ({ ...b, [g.url]: true }))} className="absolute inset-0 h-full w-full object-cover" />
                {showCaptions && g.caption && (
                  <span className={captionCls} data-testid={g._clone ? 'gallery-caption-clone' : 'gallery-caption'}>{g.caption}</span>
                )}
              </button>
            </Reveal>
            );
          })}
        </div>
      </div>

      {controls && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            disabled={!canPrev}
            aria-label="Previous"
            data-testid="gallery-prev-button"
            className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/25 text-white/90 backdrop-blur-[2px] inline-flex items-center justify-center transition-[opacity,background-color] duration-300 hover:bg-black/45 disabled:opacity-0 disabled:pointer-events-none sm:opacity-0 sm:group-hover/carousel:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            disabled={!canNext}
            aria-label="Next"
            data-testid="gallery-next-button"
            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/25 text-white/90 backdrop-blur-[2px] inline-flex items-center justify-center transition-[opacity,background-color] duration-300 hover:bg-black/45 disabled:opacity-0 disabled:pointer-events-none sm:opacity-0 sm:group-hover/carousel:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
          >
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </>
      )}

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-[rgba(10,14,20,0.96)] flex items-center justify-center"
            onClick={close}
            data-testid="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchX === null) return;
              const dx = e.changedTouches[0].clientX - touchX;
              if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
              setTouchX(null);
            }}
          >
            <button type="button" onClick={close} aria-label="Close" className="absolute top-4 right-4 sm:top-6 sm:right-6 h-11 w-11 rounded-full bg-white/10 text-white hover:bg-white/20 inline-flex items-center justify-center z-10" data-testid="gallery-lightbox-close"><X className="h-5 w-5" /></button>
            <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous image" className="hidden sm:inline-flex absolute left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 text-white hover:bg-white/20 items-center justify-center" data-testid="gallery-lightbox-prev"><ArrowLeft className="h-5 w-5" /></button>
            <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next image" className="hidden sm:inline-flex absolute right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 text-white hover:bg-white/20 items-center justify-center" data-testid="gallery-lightbox-next"><ArrowRight className="h-5 w-5" /></button>
            <motion.figure
              key={lightbox}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-[94vw] max-h-[88vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img {...imgProps(visible[lightbox].url, { widths: [800, 1280, 1600], sizes: '94vw' })} alt={visible[lightbox].alt || visible[lightbox].caption || ''} className="lightbox-img max-w-[94vw] max-h-[78vh] object-contain rounded-lg shadow-2xl" data-testid="gallery-lightbox-image" />
              <figcaption className={`mt-4 text-center ${lightboxCaptionStyle === 'accent' ? 'flex flex-col items-center gap-2.5' : 'text-white/80 text-xs tracking-[0.16em] uppercase'}`} data-testid="gallery-lightbox-caption">
                {lightboxCaptionStyle === 'accent' ? (
                  <>
                    <span className="block h-px w-10 bg-[hsl(var(--brand-gold))]" aria-hidden="true" data-testid="gallery-lightbox-accent" />
                    {visible[lightbox].caption && <span className="font-display text-base sm:text-lg text-white/95 tracking-wide">{visible[lightbox].caption}</span>}
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--brand-gold))]/80">{lightbox + 1} / {visible.length}</span>
                  </>
                ) : (
                  <>{visible[lightbox].caption} <span className="text-white/40 ml-3">{lightbox + 1} / {visible.length}</span></>
                )}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
